import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { setConfigThunk, selectConfig } from '../app/configSlice';
import { getAzureADUserToken } from '../config/aws';
import { LoginType } from '../api-types/models/LoginDto';
import { clearAppStorage } from '../utils/clearAppStorage';
import { routes } from '../config/routes';
import logging from '../config/logging';
import i18n from '../i18n';

export function useAppInit() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const config = useAppSelector(selectConfig);
    const { instance, accounts, inProgress } = useMsal();

    const loginType = localStorage.getItem('login');
    const language = localStorage.getItem('lang');

    const publicPaths = routes.map((r) => r.path);
    const isPublicRoute = publicPaths.includes(pathname);
    const isStandalonePage = ['/status', '/observer'].includes(pathname);

    useEffect(() => {
        if (inProgress !== InteractionStatus.None || isStandalonePage) {
            return;
        }

        logging.info('Loading application.');
        const requiresAuth = !isPublicRoute;

        if (
            (loginType === LoginType.BRUNCH || loginType === LoginType.COACH) &&
            !config
        ) {
            dispatch(setConfigThunk(undefined));
        } else if (!loginType && !requiresAuth && !config) {
            dispatch(setConfigThunk(undefined));
        } else if (
            loginType === LoginType.SPORTRADAR &&
            !accounts.length &&
            !config
        ) {
            dispatch(setConfigThunk(undefined));
        }

        if (loginType === LoginType.SPORTRADAR && accounts.length && !config) {
            getAzureADUserToken(instance, accounts)
                .then((token) => {
                    if (token?.idToken && token?.accessToken) {
                        dispatch(
                            setConfigThunk({
                                idToken: token.idToken,
                                accessToken: token.accessToken
                            })
                        );
                    } else if (requiresAuth) {
                        logging.warn(
                            'Invalid or missing Azure AD token, redirecting to login'
                        );
                        clearAppStorage();
                        navigate('/login');
                    }
                })
                .catch((err) => {
                    logging.error(`Error fetching Azure AD token: ${err}`);
                    if (requiresAuth) {
                        navigate('/login');
                    }
                });
        } else if (requiresAuth && !loginType) {
            logging.warn('No login type found, redirecting to login');
            navigate('/login');
        }
    }, [
        dispatch,
        navigate,
        instance,
        accounts,
        loginType,
        inProgress,
        isPublicRoute,
        isStandalonePage,
        pathname,
        config
    ]);

    useEffect(() => {
        if (language) {
            i18n.changeLanguage(language);
        }
    }, [language]);

    return {
        config,
        isStandalonePage,
        isPublicRoute,
        isConfigReady: !!config || isPublicRoute || isStandalonePage,
        pathname
    };
}
